import { marked } from 'marked'
import 'highlight.js/styles/github-dark.css'
import { nextTick, onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue'
import hljs from 'highlight.js'
import { sanitizeHtml } from './SanitizeHtml'

type ParseMarkdownOptions = {
  openLinksInNewTab?: boolean
}

export async function parseMarkdownAsync(
  text: string,
  options: ParseMarkdownOptions = {}
): Promise<string> {
  const htmlContent = await marked.parse(text)
  return sanitizeHtml(htmlContent, options)
}

export function formatPreSection(container: Ref<HTMLElement | null>): void {
  const mutationObserver = ref<MutationObserver | null>(null)

  function enhance(root: HTMLElement) {
    const codeBlocks = root.querySelectorAll<HTMLElement>('pre > code[class]')

    codeBlocks.forEach(codeElement => {
      const pre = codeElement.parentElement as HTMLElement | null
      if (!pre || pre.tagName !== 'PRE') return

      hljs.highlightElement(codeElement)

      if (pre && pre.tagName === 'PRE') {
        pre.classList.add('hljs') // optional, some themes require this
      }
    })
  }

  async function init() {
    await nextTick()
    if (!container.value) return
    enhance(container.value)

    mutationObserver.value = new MutationObserver(() => {
      if (container.value) enhance(container.value)
    })
    mutationObserver.value.observe(container.value, { childList: true, subtree: true })
  }

  onMounted(init)
  onBeforeUnmount(() => {
    mutationObserver.value?.disconnect()
    mutationObserver.value = null
  })

  // Re-run if the container element itself is swapped/replaced
  watch(container, el => {
    if (!el) return
    enhance(el)
  })
}
